import { isAxiosError } from "axios"
import {
  useGetGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet,
  useGetNwssBiothreatSignalNwssBiothreatSignalsEventIdGet,
} from "@phc/common/gen/threatAnalyticsGen"
import type { BiothreatSignal } from "@phc/common/gen/threatAnalyticsGen"

export type BiothreatSignalSource = "gdelt" | "nwss"

/** Frontend route path for a signal's standalone detail page. */
export const buildBiothreatSignalDetailPath = (
  source: BiothreatSignalSource,
  id: string
): string => `/biothreat-signals/${source}/${encodeURIComponent(id)}`

export interface BiothreatSignalByIdResult {
  signal: BiothreatSignal | undefined
  isLoading: boolean
  isError: boolean
  /** True when the API returned 404 for this id (vs. a transport error). */
  isNotFound: boolean
}

/**
 * Fetches one precomputed biothreat signal by id via the generated by-id
 * clients (`GET /{source}/biothreat-signals/{id}`). Both source-specific
 * hooks are called (Rules of Hooks) but only the one matching `source` is
 * enabled; the results are merged behind a single source-agnostic shape.
 */
export const useBiothreatSignalById = (
  source: BiothreatSignalSource | null,
  id: string | null
): BiothreatSignalByIdResult => {
  const isGdelt = source === "gdelt" && Boolean(id)
  const isNwss = source === "nwss" && Boolean(id)
  // A 404 means "no such signal" — surface it immediately instead of retrying.
  const retry = (failureCount: number, error: unknown): boolean =>
    !isNotFoundError(error) && failureCount < 3

  const gdelt = useGetGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet(
    id ?? "",
    { query: { enabled: isGdelt, retry } }
  )
  const nwss = useGetNwssBiothreatSignalNwssBiothreatSignalsEventIdGet(
    id ?? "",
    {
      query: { enabled: isNwss, retry },
    }
  )

  const active = isNwss ? nwss : gdelt
  return {
    signal: active.data,
    isLoading: active.isLoading,
    isError: active.isError,
    isNotFound: isNotFoundError(active.error),
  }
}

function isNotFoundError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404
}
